import { useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { AgentPresence } from "../utils/agent-presence-utils"
import type { SessionDateBucket } from "../utils/agents-utils"
import type { HostedAgentSession, HostedAgentSummary } from "@/api/client/hosted-agents"

import { Search, Users, X } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { cn } from "@/lib/utils"

import { AgentAvatar } from "./agent-avatar"
import { threadPresence } from "../utils/agent-presence-utils"
import { agentThreadDate } from "../utils/agent-workspace-utils"
import {
  AGENT_RAIL_HEADER_CLASS,
  AGENT_RAIL_LABEL_CLASS,
  AGENT_RAIL_META_CLASS,
  AGENT_RAIL_SECTION_CLASS,
  agentSessionOwnerName,
  agentThreadTitle,
  filterAgentSessions,
  groupSessionsByDate,
  isForeignSession,
  isSessionRunActive,
  orderAgentSessions,
  sessionVisibility,
} from "../utils/agents-utils"

type AgentThreadListProps = {
  sessions: HostedAgentSession[]
  isPending: boolean
  isError: boolean
  /** Selected thread (session id) — highlighted in the list. */
  activeSessionId?: string
  /** The agent whose character marks each thread's state; without it the list falls back to a status dot. */
  agent?: HostedAgentSummary
  /** Live presence of the open thread — beats the slower stamped run for that one row. */
  activePresence?: AgentPresence
  onSelect?: (session: HostedAgentSession) => void
  onRetry?: () => void
}

const DATE_BUCKET_LABEL_KEYS: Record<SessionDateBucket, string> = {
  today: "chatsession.today",
  past7Days: "chatsession.past_7_days",
  past30Days: "chatsession.past_30_days",
  older: "chatsession.all_time",
}

const THREAD_PRESENCE_LABEL_KEYS: Partial<Record<AgentPresence, string>> = {
  working: "agents.detail.thread_running",
  thinking: "agents.detail.thread_running",
  waiting: "agents.detail.thread_waiting",
  blocked: "agents.detail.thread_blocked",
  stopped: "agents.detail.thread_stopped",
  stopping: "agents.detail.thread_stopped",
  done: "agents.detail.thread_done",
}

type ThreadItemProps = {
  session: HostedAgentSession
  isActive: boolean
  agent?: HostedAgentSummary
  activePresence?: AgentPresence
  onSelect?: (session: HostedAgentSession) => void
}

/** The agent's own character, posed for THIS thread: bouncing while it works,
 * peeking (with an attention ring) when it waits for the user, shaking when
 * it failed, still when done. A thread's state must be readable from the
 * list without opening it. */
function ThreadPresence({
  agent,
  presence,
  label,
}: {
  agent: HostedAgentSummary
  presence: AgentPresence
  label?: string
}) {
  const attention = presence === "waiting"
  const trouble = presence === "blocked"
  return (
    <span
      className="relative flex size-5 shrink-0 items-center justify-center"
      data-thread-presence={presence}
    >
      {attention ? (
        <span
          aria-hidden="true"
          className="bg-primary/25 absolute inset-0 animate-ping rounded-full motion-reduce:animate-none"
        />
      ) : null}
      <span
        className={cn(
          "relative flex size-5 items-center justify-center rounded-full",
          attention && "ring-primary/60 ring-2",
          trouble && "ring-destructive/60 ring-2"
        )}
      >
        <AgentAvatar compact agent={agent} className="size-4" presence={presence} />
      </span>
      {label ? <span className="sr-only">{label}</span> : null}
    </span>
  )
}

/**
 * One rail row: 13 px title on a single line, one 11 px meta line. Threads
 * stamped `open` (B6) carry the "Shared" marker — and the owner's name when
 * the backend sent participants — in place of the removed group heading.
 */
function ThreadItem({ session, isActive, agent, activePresence, onSelect }: ThreadItemProps) {
  const { t, i18n } = useTranslation()
  const foreign = isForeignSession(session)
  const shared = foreign || sessionVisibility(session) === "open"
  const owner = shared ? agentSessionOwnerName(session) : undefined
  const runActive = isSessionRunActive(session)
  const date = agentThreadDate(session, i18n.resolvedLanguage ?? "en")
  const presence = isActive && activePresence ? activePresence : threadPresence(session)
  const presenceLabelKey = presence ? THREAD_PRESENCE_LABEL_KEYS[presence] : undefined
  const title = agentThreadTitle(session) || t("agents.detail.untitled_thread")
  const meta = [owner, date].filter(Boolean).join(" · ")
  // An explicit name: the row's text alone read as a run of fragments.
  const accessibleName = [
    t("agents.detail.thread_open_label", { title }),
    presenceLabelKey ? t(presenceLabelKey) : undefined,
    shared ? t("agents.detail.shared_marker") : undefined,
    meta,
  ]
    .filter(Boolean)
    .join(", ")

  return (
    <button
      aria-current={isActive ? "true" : undefined}
      aria-label={accessibleName}
      data-shared={shared ? "true" : undefined}
      data-testid="agent-thread-row"
      type="button"
      className={cn(
        "focus-visible:ring-ring flex flex-col gap-0.5 rounded-md border px-2.5 py-2 text-left transition-colors duration-150 focus-visible:ring-2 focus-visible:outline-none",
        isActive
          ? "border-border bg-accent text-accent-foreground"
          : "hover:bg-accent hover:text-accent-foreground border-transparent"
      )}
      onClick={() => onSelect?.(session)}
    >
      <span className="flex min-w-0 items-center gap-2">
        {agent && presence ? (
          <ThreadPresence
            agent={agent}
            label={presenceLabelKey ? t(presenceLabelKey) : undefined}
            presence={presence}
          />
        ) : null}
        {!(agent && presence) && runActive ? (
          <>
            {/* Status dot: rounded-full is reserved for avatars/status dots (DESIGN.md). */}
            <span
              aria-hidden="true"
              className="bg-primary size-2 shrink-0 animate-pulse rounded-full motion-reduce:animate-none"
            />
            <span className="sr-only">{t("agents.detail.thread_running")}</span>
          </>
        ) : null}
        <span className="truncate text-[13px] leading-snug font-medium">{title}</span>
      </span>
      <span className={cn(AGENT_RAIL_META_CLASS, "flex min-w-0 items-center gap-1.5")}>
        {shared ? (
          <>
            <Users aria-hidden="true" className="size-3 shrink-0" />
            <span className="shrink-0">{t("agents.detail.shared_marker")}</span>
            {meta ? <span aria-hidden="true">·</span> : null}
          </>
        ) : null}
        {meta ? <span className="truncate">{meta}</span> : null}
      </span>
    </button>
  )
}

/**
 * Thread list for one hosted agent: ONE section (label + client-side search)
 * over one list, ordered on the per-session `visibility` stamped at creation
 * (B6 — open threads first, never the agent's current memory mode) and then
 * bucketed by date like the chat sidebar. The stamped visibility is now read
 * per row as a "Shared" marker instead of a second heading.
 */
export function AgentThreadList({
  sessions,
  isPending,
  isError,
  activeSessionId,
  agent,
  activePresence,
  onSelect,
  onRetry,
}: AgentThreadListProps) {
  const { t } = useTranslation()
  const [isSearchOpen, setSearchOpen] = useState(false)
  const [query, setQuery] = useState("")
  // Focus on open only — an inline `ref` callback would re-focus on every
  // keystroke, and `autoFocus` is banned by the a11y lint rule.
  const searchRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (isSearchOpen) searchRef.current?.focus()
  }, [isSearchOpen])

  const filtered = useMemo(
    () => groupSessionsByDate(orderAgentSessions(filterAgentSessions(sessions, query))),
    [sessions, query]
  )

  let body: React.ReactNode
  if (isPending) {
    body = (
      <div className="flex flex-col gap-1.5">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="skeleton-warm h-9 rounded-md" />
        ))}
      </div>
    )
  } else if (isError) {
    body = (
      <div className="space-y-2 px-1 py-2" role="alert">
        <p className="text-muted-foreground text-sm">{t("agents.detail.threads_error")}</p>
        {onRetry ? (
          <Button size="sm" variant="outline" onClick={onRetry}>
            {t("agents.roster.retry")}
          </Button>
        ) : null}
      </div>
    )
  } else if (sessions.length === 0) {
    body = (
      <p className="text-muted-foreground px-1 py-2 text-sm">{t("agents.detail.threads_empty")}</p>
    )
  } else if (filtered.length === 0) {
    body = (
      <p className="text-muted-foreground px-1 py-2 text-sm">
        {t("agents.detail.threads_no_matches")}
      </p>
    )
  } else {
    body = (
      <div className="flex flex-col gap-0.5">
        {filtered.map((group) => (
          <div key={group.bucket} className="flex flex-col gap-0.5">
            <p className={cn(AGENT_RAIL_META_CLASS, "px-2.5 pt-1 pb-0.5")}>
              {t(DATE_BUCKET_LABEL_KEYS[group.bucket])}
            </p>
            {group.sessions.map((session) => (
              <ThreadItem
                key={session.id}
                activePresence={activePresence}
                agent={agent}
                isActive={!!session.id && session.id === activeSessionId}
                session={session}
                onSelect={onSelect}
              />
            ))}
          </div>
        ))}
      </div>
    )
  }

  return (
    <section aria-label={t("agents.detail.threads_title")} className={AGENT_RAIL_SECTION_CLASS}>
      <div className={AGENT_RAIL_HEADER_CLASS}>
        <span className={AGENT_RAIL_LABEL_CLASS}>{t("agents.detail.threads_title")}</span>
        <Button
          aria-expanded={isSearchOpen}
          aria-label={t("agents.detail.threads_search")}
          className="size-6"
          size="icon"
          variant="ghost"
          onClick={() => {
            setSearchOpen((open) => {
              if (open) setQuery("")
              return !open
            })
          }}
        >
          {isSearchOpen ? (
            <X aria-hidden="true" className="size-3.5" />
          ) : (
            <Search aria-hidden="true" className="size-3.5" />
          )}
        </Button>
      </div>
      {isSearchOpen ? (
        <Input
          ref={searchRef}
          aria-label={t("agents.detail.threads_search_placeholder")}
          className="h-8 text-[13px]"
          placeholder={t("agents.detail.threads_search_placeholder")}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
        />
      ) : null}
      {body}
    </section>
  )
}
